import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { 
  Package, 
  MapPin, 
  Phone, 
  Calendar, 
  DollarSign, 
  CheckCircle,
  Loader
} from 'lucide-react'
import { displayNGNCurrency } from '../../helpers/displayCurrency'
import { summaryApi } from '../../common'
import { useTheme } from '../../components/Theme/ThemeProvider'
import { authFetch } from '../../helpers/authFetch';

function OrderInventory() {
  const { colors } = useTheme()
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')


  console.log("Orders:", orders)

  const fetchAllOrders = async () => {
    try {
      setLoading(true)
      const response = await authFetch(summaryApi.AdminOrderInventory.url, {
        method: summaryApi.AdminOrderInventory.method,
        
      })
      
      const data = await response.json()
      if (data.success) {
        const ordersWithUserDetails = await Promise.all(
          data.orders.reverse().map(async (order) => {
            try {
              const userDetailsUrl = `${summaryApi.getUserDetailsId.url}/${order.userId}`
              const userResponse = await authFetch(userDetailsUrl, {
                method: 'GET',  
                
              })
              
              if (!userResponse.ok) return order
              
              const userData = await userResponse.json()
              return {
                ...order,
                userFirstName: userData.firstName,
                userLastName: userData.lastName
              }
            } catch (userError) {
              return order
            }
          })
        )
        setOrders(ordersWithUserDetails)
      } else {
        toast.error(data.message)
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  

  const statusHandler = async (event, orderId) => {
    try {
      const response = await authFetch(summaryApi.OrderStatus.url, {
        method: summaryApi.OrderStatus.method,
        
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, status: event.target.value })
      })
      
      const data = await response.json() 
      if (data.success) {
        toast.success(data.message)
        await fetchAllOrders()
      } else {
        toast.error(data.message)
      }
    } catch (error) {
      toast.error(error.message)
    }
  }

  useEffect(() => {
    fetchAllOrders()
  }, [])

  const getStatusColor = (status) => {
    const baseColors = {
      'Order Placed': { bg: `${colors.primary}15`, text: colors.primary, border: colors.primary },
      'Packing': { bg: `${colors.accent}15`, text: colors.accent, border: colors.accent },
      'Shipped': { bg: '#3B82F615', text: '#3B82F6', border: '#3B82F6' },
      'Out for delivery': { bg: '#8B5CF615', text: '#8B5CF6', border: '#8B5CF6' },
      'Delivered': { bg: '#10B98115', text: '#10B981', border: '#10B981' }
    }
    return baseColors[status] || baseColors['Order Placed']
  }

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      (order.userFirstName + ' ' + order.userLastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.address.phoneNumber.includes(searchTerm)
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter
    return matchesSearch && matchesStatus
  })

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold" style={{ color: colors.text }}>
          Order Management
        </h1>
        <p style={{ color: colors.textSecondary }} className="text-lg">
          Track and manage all customer orders ({filteredOrders.length})
        </p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Search */}
        <div className="relative group">
          <input
            type="text"
            placeholder="Search by customer name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 rounded-lg outline-none transition-all"
            style={{
              backgroundColor: colors.bgSecondary,
              color: colors.text,
              border: `2px solid ${colors.border}`,
              boxShadow: searchTerm ? `0 0 0 3px ${colors.primary}20` : 'none'
            }}
          />
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 rounded-lg outline-none font-medium transition-all"
          style={{
            backgroundColor: colors.bgSecondary,
            color: colors.text,
            border: `2px solid ${colors.border}`
          }}
        >
          <option value="all">All Orders</option>
          <option value="Order Placed">Order Placed</option>
          <option value="Packing">Packing</option>
          <option value="Shipped">Shipped</option>
          <option value="Out for delivery">Out for Delivery</option>
          <option value="Delivered">Delivered</option>
        </select>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="text-center space-y-4">
            <Loader className="w-12 h-12 animate-spin mx-auto" style={{ color: colors.primary }} />
            <p style={{ color: colors.textSecondary }}>Loading orders...</p>
          </div>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div 
          className="text-center py-16 rounded-xl border-2 border-dashed"
          style={{
            backgroundColor: colors.bg,
            borderColor: colors.border
          }}
        >
          <Package className="w-16 h-16 mx-auto mb-4 opacity-30" style={{ color: colors.textSecondary }} />
          <p style={{ color: colors.textSecondary }} className="text-lg font-medium">
            No orders found
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order, index) => {
            const statusColor = getStatusColor(order.status)
            return (
              <div
                key={order._id}
                className="rounded-xl border overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-[1.01] animate-slideUp"
                style={{
                  backgroundColor: colors.bgSecondary,
                  borderColor: colors.border,
                  animationDelay: `${index * 50}ms`
                }}
              >
                {/* Order Header */}
                <div 
                  className="px-6 py-4 border-b flex items-center justify-between"
                  style={{ 
                    borderColor: colors.border,
                    background: `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.accent}10 100%)`
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: `${colors.primary}20` }}
                    >
                      <Package className="w-6 h-6" style={{ color: colors.primary }} />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: colors.text }}>
                        Order ID: {order._id.slice(-8).toUpperCase()}
                      </p>
                      <p style={{ color: colors.textSecondary }} className="text-sm">
                        {new Date(order.date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: colors.primary }}>
                      {displayNGNCurrency(order.amount)}
                    </p>
                    <p style={{ color: colors.textSecondary }} className="text-xs">
                      {order.payment ? '✓ Paid' : 'Pending'}
                    </p>
                  </div>
                </div>

                {/* Order Content */}
                <div className="px-6 py-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
                  {/* Customer Info */}
                  <div className="lg:col-span-2 space-y-4">
                    <div>
                      <p style={{ color: colors.textSecondary }} className="text-sm font-semibold mb-1">
                        CUSTOMER
                      </p>
                      <p className="font-semibold text-lg" style={{ color: colors.text }}>
                        {order.userFirstName} {order.userLastName}
                      </p>
                    </div>

                    <div>
                      <p style={{ color: colors.textSecondary }} className="text-sm font-semibold mb-2">
                        DELIVERY ADDRESS
                      </p>
                      <div className="space-y-1" style={{ color: colors.text }}>
                        <div className="flex gap-2 items-start">
                          <MapPin className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: colors.primary }} />
                          <div>
                            <p className="text-sm">{order.address.street}</p>
                            <p className="text-sm">{order.address.city}, {order.address.state}, {order.address.country}</p>
                            <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                              Landmark: {order.address.landmark}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 items-center pt-2">
                          <Phone className="w-4 h-4" style={{ color: colors.primary }} />
                          <p className="text-sm">{order.address.phoneNumber}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Items & Details */}
                  <div className="lg:col-span-2 space-y-4">
                    <div>
                      <p style={{ color: colors.textSecondary }} className="text-sm font-semibold mb-2">
                        ITEMS ({order.items.length})
                      </p>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm" style={{ color: colors.text }}>
                            <span className="truncate">{item.name}</span>
                            <span className="font-semibold ml-2">×{item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p style={{ color: colors.textSecondary }} className="text-xs font-semibold mb-1">
                          METHOD
                        </p>
                        <p style={{ color: colors.text }} className="font-medium">
                          {order.paymentMethod}
                        </p>
                      </div>
                      <div>
                        <p style={{ color: colors.textSecondary }} className="text-xs font-semibold mb-1">
                          STATUS
                        </p>
                        <span 
                          className="inline-block px-3 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: statusColor.bg,
                            color: statusColor.text,
                            border: `1.5px solid ${statusColor.border}`
                          }}
                        >
                          {order.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Update */}
                <div 
                  className="px-6 py-4 border-t"
                  style={{ borderColor: colors.border }}
                >
                  <label style={{ color: colors.textSecondary }} className="text-sm font-semibold block mb-2">
                    UPDATE ORDER STATUS
                  </label>
                  <select 
                    onChange={(event) => statusHandler(event, order._id)} 
                    value={order.status}
                    className="w-full md:w-64 px-4 py-2 rounded-lg font-medium outline-none transition-all"
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.text,
                      border: `2px solid ${colors.border}`
                    }}
                  >
                    <option value="Order Placed">Order Placed</option>
                    <option value="Packing">Packing</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Out for delivery">Out for Delivery</option>
                    <option value="Delivered">Delivered</option>
                  </select>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  )
}

export default OrderInventory
